import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Button } from '../../../../../web/components/Button';
import { Dropdown } from '../../../../../web/components/Dropdown';
import { IEventRegistrationSurvey } from '../../../../models/Api';
import { styleSheet } from './styles';

type CalendarLinkProperties = {
	responseId: string;
	details: string;
	name: string;
	location: string;
	timeZone: string;
};

type DateTimeInformation = {
	startDateTimeMoment: moment.Moment;
	endDateTimeMoment: moment.Moment;
};

enum CalendarLinkType {
	GoogleCalendar = 'Google Calendar',
	Outlook = 'Outlook.com',
	ICS = 'iCal File',
	Apple = 'Apple',
	Outlook365 = 'Outlook365',
	Yahoo = 'Yahoo',
}

const calendarAction = (
	calendarLinkProperties: CalendarLinkProperties,
	dateTimeInformation: DateTimeInformation,
	calendarLinkType: CalendarLinkType
) => {
	for (const [key, value] of Object.entries(calendarLinkProperties)) {
		calendarLinkProperties[key as keyof CalendarLinkProperties] = encodeURIComponent(value);
	}
	const { details, name, location, timeZone, responseId } = calendarLinkProperties;
	const { startDateTimeMoment, endDateTimeMoment } = dateTimeInformation;
	// Note: `window.open` can return `null` if the browser fails to open the new browsing context,
	// for example because it was blocked by a browser popup blocker"
	switch (calendarLinkType) {
		case CalendarLinkType.Yahoo: {
			const startDateTime = startDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			const endDateTime = endDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			return window
				.open(
					`https://calendar.yahoo.com/?v=60&st=${startDateTime}&et=${endDateTime}&title=${name}&in_loc=${location}&desc=${details}`,
					'_blank'
				)
				?.focus();
		}
		case CalendarLinkType.Outlook365: {
			const startDateTime = startDateTimeMoment.format('YYYY-MM-DD[T]HH:mm:ss');
			const endDateTime = endDateTimeMoment.format('YYYY-MM-DD[T]HH:mm:ss');
			return window
				.open(
					`https://outlook.office.com/calendar/action/compose?rru=addevent&startdt=${startDateTime}&enddt=${endDateTime}&subject=${name}&location=${location}&body=${details}`,
					'_blank'
				)
				?.focus();
		}
		case CalendarLinkType.Apple:
		case CalendarLinkType.ICS: {
			const startDateTime = startDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			const endDateTime = endDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			return window.open(
				'data:text/calendar;charset=utf8,' +
					`BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-// Levitate App //EN\nCALSCALE:GREGORIAN\nMETHOD:PUBLISH\nBEGIN:VTIMEZONE\nTZID:${timeZone}\nX-LIC-LOCATION:${timeZone}\nEND:VTIMEZONE\nBEGIN:VEVENT\nUID:${responseId}\nDTSTART;TZID=${timeZone}:${startDateTime}\nDTEND;TZID=${timeZone}:${endDateTime}\nSUMMARY:${name}\nDESCRIPTION: ${details}\nLOCATION: ${location}\nEND:VEVENT\nEND:VCALENDAR`
			);
		}
		case CalendarLinkType.Outlook: {
			const startDateTime = startDateTimeMoment.format('YYYY-MM-DD[T]HH:mm:ss');
			const endDateTime = endDateTimeMoment.format('YYYY-MM-DD[T]HH:mm:ss');
			return window
				.open(
					`https://outlook.live.com/calendar/action/compose?rru=addevent&startdt=${startDateTime}&enddt=${endDateTime}&subject=${name}&location=${location}&body=${details}`,
					'_blank'
				)
				?.focus();
		}
		case CalendarLinkType.GoogleCalendar:
		default: {
			const startDateTime = startDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			const endDateTime = endDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			return window
				.open(
					`https://calendar.google.com/calendar/u/0/r/eventedit?dates=${startDateTime}/${endDateTime}&ctz=${timeZone}&text=${name}&location=${location}&details=${details}`,
					'_blank'
				)
				?.focus();
		}
	}
};

type Props = {
	surveyResponseId: string;
	survey: IEventRegistrationSurvey;
};

export const AddToCalendarDropdown = ({ survey, surveyResponseId }: Props) => {
	const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
	const startDateTimeMoment = moment(survey.eventInformation.startTime);
	const endDateTimeMoment = survey.eventInformation.endTime
		? moment(survey.eventInformation.endTime)
		: moment(startDateTimeMoment).add(1, 'hour');
	return (
		<Dropdown
			openOnClick
			isOpen={isDropdownOpen}
			onOpenChanged={setIsDropdownOpen}
			contentPositionY='top'
			contentClassName={css(styleSheet.dropdownMenu)}
			anchor={<Button className={css(styleSheet.dropdownButton)} kind='reverse' label='Add to Calendar' />}
		>
			<>
				{Object.values(CalendarLinkType).map(calendarType => (
					<div
						className={css(styleSheet.menuItem)}
						key={calendarType}
						onClick={() => {
							calendarAction(
								{
									name: survey.name,
									responseId: surveyResponseId,
									details: survey.eventInformation.details,
									location: survey.eventInformation.location,
									timeZone: survey.eventInformation.timeZone,
								},
								{ startDateTimeMoment, endDateTimeMoment },
								calendarType
							);
							setIsDropdownOpen(false);
						}}
					>
						{calendarType}
					</div>
				))}
			</>
		</Dropdown>
	);
};
